import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/toast';
import { trackEvent } from '@/lib/faro';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, HelpCircle } from 'lucide-react';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import type { KinkCategory, KinkRating, UserKinkRating } from '@/types';

const RATING_OPTIONS: { value: KinkRating; label: string; color: string }[] = [
    { value: 'love', label: 'Love it', color: 'bg-green-700 text-green-100' },
    { value: 'like', label: 'Like', color: 'bg-blue-700 text-blue-100' },
    { value: 'curious', label: 'Curious', color: 'bg-slate-600 text-slate-100' },
    { value: 'soft_limit', label: 'Soft limit', color: 'bg-orange-800 text-orange-100' },
    { value: 'hard_limit', label: 'Hard limit', color: 'bg-red-800 text-red-100' },
];

function RatingSelect({
    value,
    onChange,
}: {
    value: KinkRating | null;
    onChange: (val: KinkRating | null) => void;
}) {
    const current = RATING_OPTIONS.find((o) => o.value === value);

    return (
        <select
            value={value ?? ''}
            onChange={(e) => onChange((e.target.value || null) as KinkRating | null)}
            className={`w-24 rounded-md border border-slate-700 px-2 py-1 text-xs transition-colors ${
                current ? current.color : 'bg-slate-800 text-slate-500'
            }`}
        >
            <option value="">—</option>
            {RATING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
}

export default function KinksTab() {
    const { user, fetchUser } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [kinkBio, setKinkBio] = useState(user?.kink_bio ?? '');
    const bioTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

    const { data: categoriesData, isLoading: loadingKinks } = useQuery({
        queryKey: ['kink-categories'],
        queryFn: () => api.get<{ data: KinkCategory[] }>('/kinks'),
    });

    const { data: myKinksData, isLoading: loadingMyKinks } = useQuery({
        queryKey: ['my-kinks'],
        queryFn: () => api.get<{ data: Record<string, UserKinkRating> }>('/my-kinks'),
        staleTime: 0,
    });

    const categories = categoriesData?.data ?? [];
    const serverRatings = myKinksData?.data ?? {};

    const getRating = (kinkId: number): { rating_a: KinkRating | null; rating_b: KinkRating | null } => {
        const server = serverRatings[kinkId];
        if (server) return { rating_a: server.rating_a, rating_b: server.rating_b };
        return { rating_a: null, rating_b: null };
    };

    // Auto-save a single kink rating change
    const ratingMutation = useMutation({
        mutationFn: (data: { kink_id: number; rating_a: KinkRating | null; rating_b: KinkRating | null }) =>
            api.put('/my-kinks', { kinks: [data] }),
        onSuccess: () => {
            trackEvent('kinks_updated');
            queryClient.invalidateQueries({ queryKey: ['my-kinks'] });
        },
        onError: (err: Error) => toast(err.message),
    });

    const setKinkRating = (kinkId: number, field: 'rating_a' | 'rating_b', value: KinkRating | null) => {
        const current = getRating(kinkId);
        const updated = { ...current, [field]: value };
        ratingMutation.mutate({ kink_id: kinkId, ...updated });
    };

    // Auto-save kink bio with debounce
    const bioMutation = useMutation({
        mutationFn: (bio: string) => api.put('/profile', { kink_bio: bio || null }),
        onSuccess: () => {
            trackEvent('kink_bio_updated');
            fetchUser();
        },
        onError: (err: Error) => toast(err.message),
    });

    const handleBioChange = (v: string) => {
        setKinkBio(v);
        if (bioTimerRef.current) clearTimeout(bioTimerRef.current);
        bioTimerRef.current = setTimeout(() => {
            bioMutation.mutate(v);
        }, 1000);
    };

    // Filter categories/kinks by search and active category
    const filteredCategories = useMemo(() => {
        let result = categories;

        if (activeCategory) {
            result = result.filter((c) => c.slug === activeCategory);
        }

        if (search) {
            const term = search.toLowerCase();
            result = result
                .map((cat) => ({
                    ...cat,
                    kinks: cat.kinks.filter((k) => k.name.toLowerCase().includes(term)),
                }))
                .filter((cat) => cat.kinks.length > 0);
        }

        return result;
    }, [categories, activeCategory, search]);

    if (loadingKinks || loadingMyKinks) {
        return <p className="text-sm text-slate-400">Loading kinks...</p>;
    }

    return (
        <div className="space-y-6">
            {/* Kink Bio */}
            <Card>
                <CardContent className="py-4 space-y-2">
                    <label className="block text-sm font-medium">Kink bio</label>
                    <p className="text-xs text-slate-500">Describe your kink interests, experience, and what you're looking for. Visible on your profile.</p>
                    <MarkdownEditor
                        value={kinkBio}
                        onChange={handleBioChange}
                        placeholder="Tell others about your kink interests..."
                        rows={4}
                    />
                    {bioMutation.isPending && (
                        <p className="text-[10px] text-slate-600">Saving...</p>
                    )}
                </CardContent>
            </Card>

            {/* Search and category filter */}
            <div className="space-y-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search kinks..."
                        className="pl-9"
                    />
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                    <button
                        type="button"
                        onClick={() => setActiveCategory(null)}
                        className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            !activeCategory ? 'bg-violet-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                        }`}
                    >
                        All
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat.slug}
                            type="button"
                            onClick={() => setActiveCategory(cat.slug === activeCategory ? null : cat.slug)}
                            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                activeCategory === cat.slug ? 'bg-violet-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            <p className="text-xs text-slate-500">Rate your kinks and interests. This will be visible on your profile.</p>

            {/* Rating scale legend */}
            <Card>
                <CardContent className="py-3">
                    <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-300 flex items-center gap-1">
                            Rating scale <HelpCircle className="h-3 w-3 text-slate-500" />
                        </span>
                        {RATING_OPTIONS.map((opt) => (
                            <span key={opt.value} className="flex items-center gap-1">
                                <span className={`inline-block h-2 w-2 rounded-full ${opt.color.split(' ')[0]}`} />
                                <span className="text-slate-400">{opt.label}</span>
                            </span>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Kink categories */}
            {filteredCategories.map((category) => (
                <div key={category.id}>
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            {category.name}
                        </h3>
                        <div className="flex gap-4 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                            <span className="w-24 text-center">{category.kinks[0]?.column_a_label}</span>
                            <span className="w-24 text-center">{category.kinks[0]?.column_b_label}</span>
                        </div>
                    </div>
                    <Card>
                        <CardContent className="p-0">
                            {category.kinks.map((kink, idx) => {
                                const r = getRating(kink.id);
                                return (
                                    <div
                                        key={kink.id}
                                        className={`flex items-center justify-between px-4 py-2.5 ${
                                            idx > 0 ? 'border-t border-slate-800/50' : ''
                                        }`}
                                    >
                                        <span className="text-sm text-slate-200">{kink.name}</span>
                                        <div className="flex gap-4">
                                            <RatingSelect
                                                value={r.rating_a}
                                                onChange={(v) => setKinkRating(kink.id, 'rating_a', v)}
                                            />
                                            <RatingSelect
                                                value={r.rating_b}
                                                onChange={(v) => setKinkRating(kink.id, 'rating_b', v)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>
                </div>
            ))}

            {filteredCategories.length === 0 && (
                <p className="text-center text-sm text-slate-500 py-8">No kinks match your search.</p>
            )}
        </div>
    );
}
